import { ref, computed } from 'vue';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { chartTheme } from '@rdrive/theme';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';

// Column sets per page (standalone AM's COLUMN_PRESETS, verbatim). Optional columns drop out when
// the project does not populate the underlying field (`field` → the `hasField` prop).
const COLUMN_PRESETS = {
    overview: ['assetRefId', 'miniMap', 'photo', 'drawingTitle', 'createdByName', 'assetSystem', 'manufacturer', 'responsibleParty', 'statusTitle'],
    inspection: ['assetRefId', 'miniMap', 'drawingTitle', 'assetSystem', 'latestInspectionDate', 'nextInspectionDate', 'rating', 'statusTitle'],
    data: ['assetRefId', 'photo', 'assetSystem', 'manufacturer', 'installationCost', 'latestInspectionDate', 'passRate', 'statusTitle'],
};

// Standalone AM's COLUMN_DEFS, verbatim: `titleKey` is a key of ./messages (translated here — the
// table shows a `title` as-is), `key` the sort key the app's fetchPage maps, `field` the
// BI-Asset alias that gates the column.
const COLUMN_DEFS = {
    assetRefId: { titleKey: 'assetId', key: 'assetRefId', align: 'start', sortable: true },
    miniMap: { titleKey: 'miniMap', key: 'miniMap', align: 'start', sortable: false },
    photo: { titleKey: 'photo', key: 'photo', align: 'center', sortable: false, field: 'photo' },
    drawingTitle: { titleKey: 'drawing', key: 'drawingTitle', align: 'start', sortable: true, width: '180px' },
    createdByName: { titleKey: 'createdBy', key: 'createdByName', align: 'start', sortable: true, width: '150px' },
    assetSystem: { titleKey: 'details', key: 'assetSystem', align: 'start', sortable: true, width: '200px' },
    manufacturer: { titleKey: 'manufacturer', key: 'manufacturer', align: 'start', sortable: true, width: '180px', field: 'manufacturer' },
    responsibleParty: { titleKey: 'responsibleParty', key: 'responsibleParty', align: 'start', sortable: true, width: '150px' },
    latestInspectionDate: { titleKey: 'latestInspection', key: 'latestInspectionDate', align: 'start', sortable: true, field: 'inspectionDate' },
    nextInspectionDate: { titleKey: 'nextInspection', key: 'nextInspectionDate', align: 'start', sortable: true, field: 'nextInspectionDate' },
    installationCost: { titleKey: 'installationCost', key: 'installationCost', align: 'start', sortable: true, field: 'installationCost' },
    rating: { titleKey: 'rating', key: 'rating', align: 'start', sortable: true, field: 'rating' },
    passRate: { titleKey: 'passRate', key: 'passRate', align: 'start', sortable: true, field: 'passRate' },
    statusTitle: { titleKey: 'status', key: 'statusTitle', align: 'start', sortable: true, width: '160px' },
};

// The table's initial sort (AM's BaseGrid `default-sort`): Asset ID ascending — the app's
// fetchPage sorts it by the ref's prefix, then numerically (AHU-2 < AHU-10)
const DEFAULT_SORT = [{ key: 'assetRefId', order: 'asc' }];

// The page-size menu: AM's BaseGrid default (the table opens at 10 rows, which the menu does not
// offer — as in AM, and as IssuesGrid)
const PAGE_SIZES = [
    { title: '50', value: 50 },
    { title: '100', value: 100 },
    { title: '150', value: 150 },
    { title: '200', value: 200 }
];

const EMPTY_VALUE = '—';

// Colours — @rdrive/theme tokens, the same in both modes: the overdue chip + text are
// `chart.semantic.serious` (AM's `statusColors.serious`); the photo border and the no-drawing
// placeholder are the drawing thumbnail's frame (`chart.pin.frameBackground` / `frameBorder` —
// AM's own literals for the two, bar the placeholder's dashed border: AM 0.35 alpha, the frame
// token 0.25), handed to ./style.css as custom properties.
const THEME = chartTheme();
const OVERDUE_COLOR = THEME.semantic.serious;
const FRAME_VARS = Object.freeze({
    '--rdrive-assets-grid-frame-bg': THEME.pin.frameBackground,
    '--rdrive-assets-grid-frame-border': THEME.pin.frameBorder
});

// Standalone AM's `formatDate` (src/utils/formatters.js), verbatim: ISO strings, Date objects and
// Arrow's epoch numbers → "05 Mar 2026"; anything unparseable → ''.
const formatDate = (value, pattern = 'dd MMM yyyy') => {
    if (value === null || value === undefined || value === '') return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, pattern);
};

export default (props, { emit }) => {
    // LOCAL composer with the warn flags off (@rdrive/i18n): every string of the grid is here; the
    // search field and the phone table's texts are DuckDbDataTable's, the thumbnail's
    // @rdrive/plugin-drawing-pin's, the photo dialog's ItemPhotoDialog's.
    const { t } = useComponentI18n(messages);

    // The preset's columns, gated by `hasField` (called here, so a reactive one re-gates them) and
    // translated (the table shows a `title` as-is)
    const headers = computed(() =>
        (COLUMN_PRESETS[props.preset] || COLUMN_PRESETS.overview)
            .map((key) => COLUMN_DEFS[key])
            .filter((column) => column && (!column.field || props.hasField(column.field)))
            .map(({ titleKey, field, ...column }) => ({ ...column, title: t(titleKey) }))
    );

    // Photo dialog — which photos, in AM's order (photo, response photo, signature); the sibling
    // ItemPhotoDialog owns the carousel and starts it at the first photo whenever it opens
    const showPhotoDialog = ref(false);
    const selectedPhotos = ref([]);
    const openPhotoDialog = (item) => {
        selectedPhotos.value = [item.photo, item.responsePhoto, item.signature].filter(Boolean);
        showPhotoDialog.value = true;
    };

    // Helpers (standalone AM's, verbatim)
    const isPriority = (item) => String(item.priority).toLowerCase() === 'true';

    const formatPassRate = (passRate) => (passRate === null || passRate === undefined ? EMPTY_VALUE : `${Math.round(passRate)}%`);

    const daysText = (item) => {
        if (!item?.nextInspectionDate) return '';
        const dueDate = new Date(item.nextInspectionDate);
        if (isNaN(dueDate.getTime())) return '';
        const daysLeft = differenceInDays(startOfDay(dueDate), startOfDay(new Date()));
        if (daysLeft < 0) return t('daysOverdue', { days: Math.abs(daysLeft) });
        return t('daysLeft', { days: daysLeft });
    };

    // The asset in the rdrive web app — through the app's opener (asset-management: a new tab, and
    // nothing in demo mode, where no rdrive project is behind the data)
    const openItemUrl = (item) => {
        props.openUrl(`https://${props.apiHost}/${props.projectCode}/assets?asset-client-ids=${item.assetClientId}`);
    };

    // DuckDbDataTable's itemsLoaded, forwarded (asset-management fetches the page's pins on it)
    const onItemsLoaded = (payload) => emit('itemsLoaded', payload);

    return {
        t,
        headers,
        defaultSort: DEFAULT_SORT,
        pageSizes: PAGE_SIZES,
        emptyValue: EMPTY_VALUE,
        overdueColor: OVERDUE_COLOR,
        frameVars: FRAME_VARS,
        showPhotoDialog,
        selectedPhotos,
        openPhotoDialog,
        isPriority,
        formatPassRate,
        formatDate,
        daysText,
        openItemUrl,
        onItemsLoaded
    };
};
